/** @format */

import React, { FC, useState, forwardRef, useImperativeHandle, useMemo } from 'react'
import {
  Message,
  Select,
  Option,
  Button,
  Radio,
  Icon,
  Table,
  TableColumn,
  Timepicker,
  Spinner,
  _$
} from '@king-design/react'
import styled from 'styled-components'
import { PeriodOpts, SuppressDurationOpts, initEventRuleData, EventRuleProps } from './constants'
import BatchEditDialog from './BatchEditDialog'
import TagDialog from './TagDialog'
import { Divider } from 'src/components'
import { useDeepCompareEffect } from 'react-use'

interface SelectEventTableProps {
  className?: string
  hidden?: boolean
  productType?: number
  eventList: any[]
  eventRules?: EventRuleProps[]
  validate?: boolean
  onChange?: (values: EventRuleProps[]) => void
}

const SelectEventTable: FC<SelectEventTableProps> = forwardRef(
  (
    { className, hidden, productType = 999, eventList, eventRules, validate = true, onChange },
    ref
  ) => {
    const [radio, setRadio] = useState<1 | 2>(1)
    const [checkedKeys, setCheckedKeys] = useState<string[]>([])
    // 批量编辑
    const [editVisible, setEditVisible] = useState<boolean>(false)
    // 维度设置
    const [tagData, setTagData] = useState<any>({})
    const [tagVisible, setTagVisible] = useState<boolean>(false)
    const onTableChange = (values, index) => {
      onChange(eventRules?.map((item, i) => (i === index ? values : item)))
    }

    const tagsObject = useMemo(() => {
      const result = {}
      if (eventList?.length) {
        eventList?.forEach(item => {
          const { name, eventTags } = item
          if (eventTags?.length) {
            result[name] = eventTags
          }
        })
      }
      return result
    }, [eventList])

    const haveTags = useMemo(() => {
      return eventRules?.some(item => tagsObject?.[item?.eventName] || !!item.eventTags?.length)
    }, [eventRules, tagsObject])

    useDeepCompareEffect(() => {
      if (!eventRules?.length) {
        onRadioChange(1)
      } else {
        setRadio(eventRules?.length === eventList?.length ? 2 : 1)
      }
    }, [eventList, eventRules])

    // // radio onChange
    const onRadioChange = value => {
      setRadio(value)
      setCheckedKeys([])
      if (eventList?.length) {
        if (value === 2) {
          onChange(
            eventList?.map(({ name }) => {
              const findRule = eventRules.find(rule => rule.eventName === name)
              return (
                findRule || {
                  ...initEventRuleData,
                  eventName: name
                }
              )
            })
          )
        }
      }
    }

    useImperativeHandle(ref, () => ({
      getEventRules: () => {
        return eventRules
      }
    }))
    return (
      <div className={className} style={{ display: hidden ? 'none' : 'block' }}>
        <div className="event_radios">
          <Radio
            name="eventRadio"
            trueValue={1}
            value={radio}
            onChangeValue={value => {
              onRadioChange(value)
            }}
          >
            {_$('自选事件')}
          </Radio>
          <Radio
            name="eventRadio"
            trueValue={2}
            value={radio}
            onChangeValue={value => {
              onRadioChange(value)
            }}
          >
            {_$('全部事件')}
          </Radio>
        </div>
        <div className="event_table">
          <div className="event_table_buttons">
            <Button
              type="primary"
              disabled={eventRules?.length === eventList?.length}
              onClick={() => {
                if (!validate || eventRules.every(({ eventName }) => !!eventName)) {
                  onChange([...eventRules, initEventRuleData])
                } else {
                  Message.warning(_$('请选择事件名称'))
                }
              }}
            >
              <Icon className="ion-plus-round" />
              {_$('添加事件')}
            </Button>
            <Button
              type="primary"
              disabled={!checkedKeys?.length}
              onClick={() => {
                setEditVisible(true)
              }}
            >
              <Icon className="ion-edit" />
              {_$('批量编辑')}
            </Button>
            <Button
              type="primary"
              disabled={!checkedKeys?.length}
              onClick={() => {
                const deldata = eventRules.filter(item => !checkedKeys.includes(item.eventName))
                if (!validate || deldata?.length) {
                  onChange(deldata)
                  setCheckedKeys([])
                } else {
                  Message.error(_$('请至少保留一条事件'))
                }
              }}
            >
              <Icon className="ion-trash-a" />
              {_$('批量删除')}
            </Button>
          </div>
          <Table
            data={eventRules}
            rowCheckable={false}
            type="grid"
            fixHeader="520"
            animation={false}
            rowKey={({ eventName }, index) => eventName || index + ''}
            checkedKeys={checkedKeys}
            disableRow={value => !value.eventName}
            onChangeCheckedKeys={keys => {
              setCheckedKeys(keys)
            }}
          >
            <TableColumn
              title={_$('事件名称')}
              key="name"
              width={200}
              fixed="left"
              slotTemplate={([data, index]) => {
                return eventList?.length ? (
                  <Select
                    filterable={true}
                    value={data.eventName}
                    onChangeValue={eventName => {
                      const findEvent = eventList.find(event => event.name === eventName)
                      onTableChange(
                        {
                          ...initEventRuleData,
                          eventNameDesc: findEvent.nameDesc || [],
                          eventName
                        },
                        index
                      )
                    }}
                  >
                    {eventList?.map(({ name, nameDesc }) => (
                      <Option
                        disabled={!!eventRules.find(item => item.eventName === name)}
                        key={name}
                        value={name}
                      >
                        {nameDesc}
                      </Option>
                    ))}
                  </Select>
                ) : null
              }}
            />
            {haveTags ? (
              <TableColumn
                title={_$('维度')}
                key="tags"
                width={200}
                slotTemplate={([data, index]: [EventRuleProps, number]) => {
                  const { eventTags, eventName } = data
                  return (
                    <div className="tags_td">
                      {tagsObject?.[eventName]?.length ? (
                        eventTags?.length ? (
                          eventTags?.map(item => {
                            const { tagName, tagValue } = item
                            return (
                              <p className="text_overflow" key={tagName}>
                                {tagName}={tagValue}
                              </p>
                            )
                          })
                        ) : (
                          <span style={{ color: '#999' }}>{_$('默认对该事件的所有维度生效')}</span>
                        )
                      ) : (
                        '-'
                      )}
                    </div>
                  )
                }}
              />
            ) : null}
            <TableColumn
              width={735}
              title={_$('告警策略')}
              key="event_policy"
              slotTemplate={([data, index]: [EventRuleProps, number]) => {
                const { effectBT, effectET, period, accumulatedCount, suppressDuration } = data
                return (
                  <div className="event_policy_td">
                    <Timepicker
                      width={135}
                      value={[effectBT, effectET]}
                      step="00:15:00"
                      range
                      valueFormat="HH:mm"
                      showFormat="HH:mm"
                      onChangeValue={value => {
                        if (value[0] !== effectBT || value[1] !== effectET) {
                          onTableChange({ ...data, effectBT: value[0], effectET: value[1] }, index)
                        }
                      }}
                    />
                    {period ? (
                      <Select
                        width={140}
                        value={period}
                        onChangeValue={value => {
                          onTableChange({ ...data, period: value }, index)
                        }}
                      >
                        {Object.keys(PeriodOpts).map(key => (
                          <Option value={Number(key)} key={key}>
                            {PeriodOpts[key]}
                          </Option>
                        ))}
                      </Select>
                    ) : (
                      <div style={{ width: 140 }}></div>
                    )}
                    <Select
                      width={100}
                      value={period ? 2 : 1}
                      onChangeValue={value => {
                        const isTrigger = value === 1
                        onTableChange(
                          {
                            ...data,
                            period: isTrigger ? undefined : 300,
                            accumulatedCount: 1
                          },
                          index
                        )
                      }}
                    >
                      <Option value={1}>{_$('触发')}</Option>
                      <Option value={2}>{_$('累计触发')} </Option>
                    </Select>
                    <div>
                      <Spinner
                        width={50}
                        min={period ? 2 : 1}
                        max={180}
                        step={1}
                        forceStep
                        value={accumulatedCount}
                        disabled={!period}
                        onChangeValue={value => {
                          onTableChange({ ...data, accumulatedCount: value }, index)
                        }}
                      ></Spinner>
                      {_$('次则')}
                    </div>
                    <Select
                      width={150}
                      value={suppressDuration}
                      onChangeValue={value => {
                        onTableChange({ ...data, suppressDuration: value }, index)
                      }}
                    >
                      {Object.keys(SuppressDurationOpts).map(key => (
                        <Option value={Number(key)} key={key}>
                          {_$(SuppressDurationOpts[key])}
                        </Option>
                      ))}
                    </Select>
                  </div>
                )
              }}
            />
            <TableColumn
              title={_$('操作')}
              key="active"
              width={105}
              fixed="right"
              slotTemplate={([data, index]) => {
                const { eventName } = data
                return (
                  <div className="table_active">
                    {!validate || eventRules?.length > 1 ? (
                      <a
                        onClick={() => {
                          onChange(eventRules.filter((item, i) => i !== index))
                        }}
                      >
                        {_$('删除')}
                      </a>
                    ) : null}
                    {eventRules?.length > 1 && tagsObject?.[eventName]?.length ? <Divider /> : null}
                    {tagsObject?.[eventName]?.length ? (
                      <a
                        onClick={() => {
                          setTagData(data)
                          setTagVisible(true)
                        }}
                      >
                        {_$('维度')}
                      </a>
                    ) : null}
                  </div>
                )
              }}
            />
          </Table>
        </div>
        <BatchEditDialog
          visible={editVisible}
          onOk={data => {
            onChange(
              eventRules?.map((item, i) => {
                if (checkedKeys.includes(item.eventName)) {
                  return { ...item, ...data }
                } else {
                  return item
                }
              })
            )
            setEditVisible(false)
          }}
          onClose={() => {
            setEditVisible(false)
          }}
        />
        <TagDialog
          visible={tagVisible}
          eventData={tagData}
          tagList={tagsObject?.[tagData?.eventName] || []}
          onOk={data => {
            onChange(
              eventRules?.map((item, i) => {
                if (item.eventName === tagData?.eventName) {
                  return { ...item, eventTags: data }
                } else {
                  return item
                }
              })
            )
            setTagData({})
            setTagVisible(false)
          }}
          onClose={() => {
            setTagData({})
            setTagVisible(false)
          }}
        />
      </div>
    )
  }
)

export default styled(SelectEventTable)`
  margin-top: 8px;
  .event_radios {
    .k-radio {
      margin-right: 16px;
    }
  }
  .k-card {
    margin-top: 8px;
    .k-card-body {
      padding: 16px;
    }
  }
  .event_table {
    margin-top: 8px;
    .event_table_buttons {
      .k-btn {
        margin-right: 8px;
      }
    }
    .k-table {
      margin-top: 8px;
      .k-spinner {
        margin-right: 6px;
      }
      .table_active {
        a {
          margin: 0 8px;
        }
      }
      thead tr th:first-child,
      tbody tr td:first-child {
        .k-checkbox {
          margin-bottom: 0;
        }
      }
      .k-select {
        width: 100%;
      }
      .event_policy_td {
        display: flex;
        align-items: center;
        & > * {
          margin-right: 8px;
        }
      }
      .tags_td {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        p {
          background: rgb(230, 247, 255);
          color: rgb(0, 145, 234);
          margin: 2px;
          border-radius: 4px;
          padding: 0 8px;
          word-break: break-all;
        }
      }
    }
  }
`
